module Client.Components.ServiceForm

open Client
open Client.Services
open Shared
open global.Elmish
open Fable.Core.JsInterop
open System
open Thoth.Fetch
open Thoth.Json
open Fetch.Types
open Feliz
open Feliz.MaterialUI
open Fable.MaterialUI.Icons
open Fable.MaterialUI.MaterialDesignIcons
open Feliz.SweetAlert
open Feliz.UseElmish
open Fable.Core.Experimental
open Fable.Core.JS
open FsToolkit.ErrorHandling
open CustomerDexImaging.Models

let strOption (s : string) = if String.IsNullOrWhiteSpace s then None else Some s

let parseInt (s : string) =
  if String.IsNullOrWhiteSpace s
  then None
  else
    match Int32.TryParse s with
    | false, _ -> None
    | true, v -> if v > 0 then Some v else None

type FieldState =
  { Validate : bool
    FieldValue : string option
    FieldWarning : string option
    InitialValue : string option } with
    static member defaultValue =
      { Validate = false; InitialValue = None; FieldValue = None; FieldWarning = None }
    static member init v =
      { Validate = false; InitialValue = v; FieldValue = v; FieldWarning = None }
    member this.setValue v =
      { this with Validate = true; FieldValue = v; FieldWarning = if v.IsNone then Some "A value is required." else None }
    member this.getValue () =
      this.FieldValue |> Result.requireSome "A value is required."

type EntryState =
  { FirstAndLastName : FieldState
    CompanyName : FieldState
    ServiceAddress : FieldState
    EmailAddress : FieldState
    BusinessPhone : FieldState
    EquipmentId : FieldState
    Make : FieldState
    Model : FieldState
    Description : FieldState
    FailureMessage : string option } with
    member this.validate () =
      result {
        let! firstAndLastName = this.FirstAndLastName.getValue ()
        let! companyName = this.CompanyName.FieldValue |> Result.requireSome "A value is required."
        let! serviceAddress = this.ServiceAddress.FieldValue |> Result.requireSome "A value is required."
        let! emailAddress = this.EmailAddress.FieldValue |> Result.requireSome "A value is required."
        let! businessPhone = this.BusinessPhone.FieldValue |> Result.requireSome "A value is required."
        let! equipmentId = this.EquipmentId.FieldValue |> Result.requireSome "A value is required"
        let! make = this.Make.FieldValue |> Result.requireSome "A value is required"
        let! model = this.Model.FieldValue |> Result.requireSome "A value is required"
        let! description = this.Description.FieldValue |> Result.requireSome "A value is required"
        let validatedEntry =
          { ServiceFormRequest.FirstAndLastName = firstAndLastName
            CompanyName = companyName
            ServiceAddress = serviceAddress
            EmailAddress = emailAddress
            BusinessPhone = businessPhone
            EquipmentId = equipmentId
            Make = make
            Model = model
            Description = description
            RecaptchaResponse = ""
            FailureMessage = this.FailureMessage
          }

        return validatedEntry
      }
    member this.isValid () =
      match this.validate() with
      | Ok _ -> true
      | Error _ -> false
    static member defaultValue () =
      { FirstAndLastName = FieldState.defaultValue
        CompanyName = FieldState.defaultValue
        ServiceAddress = FieldState.defaultValue
        EmailAddress = FieldState.defaultValue
        BusinessPhone = FieldState.defaultValue
        EquipmentId = FieldState.defaultValue
        Make = FieldState.defaultValue
        Model = FieldState.defaultValue
        Description = FieldState.defaultValue
        FailureMessage = None }
    static member init (equipmentId : string) (failureMessage : string) =
      { (EntryState.defaultValue ()) with EquipmentId = FieldState.init (Some equipmentId); FailureMessage = Some failureMessage; }

type Model =
  | Initialized
  | Sending
  | Sent of string

type Msg =
  | SubmitForm of ServiceFormRequest
  | SendingResult of Result<string,string>

let submitForm (request : ServiceFormRequest) : Promise<string> =
  let decoder = Services.decoder<string>
  Fetch.post<_,string>("/api/Service/RequestForm", request, decoder = decoder)

let submitFormCmd request =
  Cmd.OfPromise.either submitForm request
    (Ok >> SendingResult)
    (fun x ->
      "Unable to submit request. Please retry" |> Error |> SendingResult)

let init () =
  Initialized, Cmd.none

let update msg model =
  match msg with
  | SubmitForm r ->
    Sending, submitFormCmd r
  | SendingResult r ->
    match r with
    | Ok v -> Sent v, Cmd.none
    | Error msg ->
      let alert =
        Cmd.Swal.fire [
            swal.title "Unable to process request. Please try again."
            swal.text msg ]
      Initialized, alert

let pendingHeaderMessage =
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Mui.typography [
            typography.variant.body1
            typography.children "Please fill out this form to request service."
          ]
        ]
      ]
    ]
  ]

let view = React.functionComponent (fun (inputProps: {| initialValues : EntryState option; reCaptchaSiteKey : string; returnResult: unit -> unit; |}) ->
  let state, setState = React.useState(inputProps.initialValues |> Option.defaultWith EntryState.defaultValue)
  let model, dispatch = React.useElmish(init (), update, [| |])
  let warningState, setWarningState = React.useState<string option> None
  let classes = Styles.useStyles()
  let effect () = ReCaptcha.initialize inputProps.reCaptchaSiteKey
  Feliz.React.useEffect (effect , [||])
  match model with
  | Sent msg ->
    Html.div [
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.grid [
                grid.item true
                grid.children [
                  Mui.typography [
                    typography.variant.body1
                    typography.children "Thank you. Your service request has been received, and you will receive a notification when your request has been processed."
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.grid [
                grid.item true
                grid.children [
                  Mui.button [
                    prop.type'.submit
                    button.fullWidth true
                    button.variant.contained
                    button.color.primary
                    button.children "Add Another Request"
                    prop.onClick (fun e ->
                      e.preventDefault ()
                      inputProps.returnResult()
                    )
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
    ]
  | Sending
  | Initialized ->
    Html.div [
      pendingHeaderMessage
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Html.form [
                prop.children [
                  Mui.textField [
                    textField.placeholder "Enter Your First and Last Name"
                    textField.defaultValue (state.FirstAndLastName.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "First & Last Name"
                    textField.name "FirstAndLastName"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with FirstAndLastName = strOption !!ev.target?value |> state.FirstAndLastName.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder "Company Name"
                    textField.defaultValue (state.CompanyName.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Company"
                    textField.name "CompanyName"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with CompanyName = strOption !!ev.target?value |> state.CompanyName.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder ""
                    textField.defaultValue (state.ServiceAddress.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Service Address"
                    textField.name "ServiceAddress"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with ServiceAddress = strOption !!ev.target?value |> state.ServiceAddress.setValue })
                  ]
                  let emailHandler emailOption = setState { state with EmailAddress = state.EmailAddress.setValue emailOption }
                  Components.EmailAddress.view {| initialValue = state.EmailAddress.InitialValue; allowEntry = true; isRequired = true; returnResult = emailHandler |}
                  Components.PhoneNumber.view {| initialValue = state.BusinessPhone.InitialValue; allowEntry = true; isRequired = true; returnResult = (fun v -> setState { state with BusinessPhone = state.BusinessPhone.setValue v }) |}
                  Mui.textField [
                    textField.placeholder "Equipment ID (Asset Number)"
                    textField.defaultValue (state.EquipmentId.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Equipment ID"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) ->
                      setState { state with EquipmentId = strOption !!ev.target?value |> state.EquipmentId.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder "Equipment Make"
                    textField.defaultValue (state.Make.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Make"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) ->
                      setState { state with Make = strOption !!ev.target?value |> state.Make.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder "Equipment Model"
                    textField.defaultValue (state.Model.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Model"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) ->
                      setState { state with Model = strOption !!ev.target?value |> state.Model.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder "Describe the type of service needed"
                    textField.defaultValue (state.Description.InitialValue |> Option.defaultValue "")
                    textField.multiline true
                    textField.rows 4
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Service Request"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) ->
                      setState { state with Description = strOption !!ev.target?value |> state.Description.setValue })
                  ]

                  let buttonProgressIndicator =
                    Mui.circularProgress [
                      prop.className classes.buttonProgress
                      prop.size 24
                    ]


                  Mui.grid [
                    grid.container true
                    grid.spacing._2
                    grid.children [
                      Mui.grid [
                        grid.item true
                        grid.xs._6
                        grid.children [
                          Html.div [
                            prop.className classes.wrapper
                            prop.children [
                              Mui.button [
                                prop.type'.submit
                                button.fullWidth true
                                button.variant.contained
                                button.color.primary
                                button.children "Submit"
                                button.disabled (state.isValid() |> not || model = Sending)
                                prop.onClick (fun e ->
                                  e.preventDefault ()
                                  match state.validate() with
                                  | Ok v ->
                                    // inputProps.returnResult () // (Some v)
                                    let fn token =
                                      SubmitForm { v with RecaptchaResponse = token; } |> dispatch
                                    ReCaptcha.registerCallBack inputProps.reCaptchaSiteKey Constants.recaptchaRequestServiceAction fn
                                  | Error msg -> setWarningState (Some msg)
                                )
                              ]
                              if model = Sending then buttonProgressIndicator else Html.none
                            ]
                          ]
                        ]
                      ]
                      Mui.grid [
                        grid.item true
                        grid.xs._6
                        grid.children [
                          Mui.button [
                            button.fullWidth true
                            button.variant.contained
                            button.color.default'
                            button.children "Cancel"
                            prop.onClick (fun e ->
                              e.preventDefault ()
                              inputProps.returnResult()
                            )
                          ]
                        ]
                      ]
                      if warningState.IsSome then Html.text warningState.Value else Html.none
                    ]
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
    ]
)